<template>
    <div class="mx-auto w-full max-w-[1168px] pb-40 px-8 pt-0">
        <template v-if="state==='loaded'">
            <ui-header :title="'Edit Draft Request for Materials #'+requestForMaterials.reference"
                       :subtitle="requestForMaterials.id"
                       class="sticky sticky-top top-0 pt-6 pb-4 bg-white z-30">
                <template #actions>
                    <ui-button-new icon="fas fa-trash" danger @click="openDeleteModal">Delete</ui-button-new>
                </template>
            </ui-header>
            <div class="grid gap-6 grid-cols-12 pb-6 pt-0">
                <ui-data-grid-item class="xl:col-span-12" label="Package"
                                   :value="requestForMaterials.packageName"></ui-data-grid-item>
                <ui-date-picker label="Requested date:" name="dateTime"
                                v-model="requestForMaterials.requestedFor"
                                class="xl:col-span-12"
                                placeholder="What is the date for the requested materials?"
                                :with-time="false"
                                @input="save()" @blur="requestForMaterials.refresh()"></ui-date-picker>
                <ui-textarea autoresize label="Notes" name="notes"
                             v-model="requestForMaterials.notes"
                             placeholder="Notes for this Request for Materials..."
                             class="xl:col-span-12" @input="save" @blur="requestForMaterials.refresh()"></ui-textarea>
            </div>
            <div class="mt-6">
                <h2 class="text-md font-semibold text-gray-700 mb-2">Materials Required</h2>
                <div
                    class="flex mt-4 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                    <div class="w-6"></div>
                    <div class="border-l border-gray-200 px-2 py-1 -mx-px flex-grow">Material</div>
                    <div class="border-l border-gray-200 px-2 py-1 -ml-px w-16 flex">UoM</div>
                    <div class="border-l border-gray-200 px-2 py-1 -ml-px w-16 flex">Qty</div>
                    <div class="border-l border-gray-200 px-2 py-1 -ml-px w-1/3 flex">Resource Planning Report Item
                    </div>
                    <div class="border-l w-8"></div>
                </div>
                <draggable :list="requestForMaterials.items" draggable=".draggable" handle=".handle"
                           group="material"
                           @change="requestForMaterials.saveAndRefresh()"
                >
                    <request-for-material-item v-for="(item,key) in requestForMaterials.items" :key="key"
                                               @addMaterials="addMaterials"
                                               :resource-planning-report="resourcePlanningReport"
                                               :item="item"
                                               :request-for-materials="requestForMaterials"
                                               ref="items"
                                               @removeMaterial="removeMaterialItem"
                                               @removeItemMaterial="removeItemMaterialItem"
                                               @removeTaskMaterial="removeTaskMaterialItem"
                                               @removeSectionMaterial="removeSectionMaterialItem"
                                               @changed="save"
                    ></request-for-material-item>
                </draggable>
                <!-- New Task Item. -->
                <div class="flex border-t border-b -mt-px border-gray-200 bg-white">
                    <!-- Draggable handle. -->
                    <div class="w-6 flex-shrink-0 self-start py-1.5">
                        <div class="w-5 h-5 flex items-center">
                            <i class="fa fa-plus text-xs text-gray-300 pl-1 pt-1"></i>
                        </div>
                    </div>
                    <!-- Material. -->
                    <div class="flex flex-grow -ml-px bg-white">
                        <page-cell
                            class="text-sm -my-px px-2 py-1.5 border border-gray-200"
                            :placeholder="'Add new material item...'"
                            v-model="newMaterial"
                            @input="addItem('material')"
                        ></page-cell>
                    </div>
                    <div class="w-8"></div>
                </div>
            </div>
            <ui-data-grid-item label="Quick action" wide class="mt-8" v-if="requestForMaterials.status==='Draft'">
                <div class="flex flex-col md:flex-row flex-wrap -m-2">
                    <ui-button-new class="m-2" primary icon="fas fa-check" @click="submit" :loading="busy">Submit for
                        Action
                    </ui-button-new>
                </div>
            </ui-data-grid-item>

            <ui-data-grid class="mt-8">
                <ui-data-grid-item label="Created By">{{ requestForMaterials.createdBy }} on
                    {{ requestForMaterials.createdOn }}
                </ui-data-grid-item>
                <ui-data-grid-item label="Last Updated By" v-if="requestForMaterials.lastUpdatedBy">{{
                        requestForMaterials.lastUpdatedBy
                    }} on {{
                        requestForMaterials.lastUpdatedOn
                    }}
                </ui-data-grid-item>
            </ui-data-grid>

        </template>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import UiContainer from "../../ui/UiContainer.vue";
import UiDatePicker from "../../ui/UiDatePicker.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import UiHeader from "../../ui/UiHeader.vue";
import UiInput from "../../ui/UiInput.vue";
import UiToggle from "../../ui/UiToggle.vue";
import {ProjectDeliveryRequestForMaterials as RequestForMaterials, ResourcePlanningReport} from "@models";
import moment from "moment";
import {debounce} from "lodash";
import RequestForMaterialItem from "./RequestForMaterialItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import {uuid} from "../../../utils";

export default {
    components: {
        UiDataGrid,
        UiDropdownItem, UiDropdown,
        RequestForMaterialItem,
        draggable,
        UiToggle, UiInput, UiHeader, UiDataGridItem, UiDatePicker, UiButtonNew, UiContainer
    },
    props: {
        item: Object,
        packageReference: Number,
        requestForMaterialsId: '',
        fsModal: Object,
        removeItem: Function,
    },
    data() {
        return {
            requestForMaterials: {},
            resourcePlanningReport: Object,
            contractRender: 0,
            busy: false,
            dateTimeError: '',
            moment,
            state: 'loading',
            newMaterial: '',
            newQuantity: '',
            newUom: '',
            grouped: false,
            groupedItems: [],
            renderKey: 0,
            debouncedUpdate: debounce(this.update, 300),
        }
    },
    computed: {},
    methods: {
        addMaterials(materials) {
            materials.forEach(addingMaterial => {
                let {material, section, task, item} = addingMaterial;
                this.requestForMaterials.items.push({
                    id: uuid(),
                    material: material.name,
                    quantity: material.required,
                    uom: material.unitOfMeasure,
                    itemReference: {
                        section: {id: section.id, name: section.name},
                        task: {id: task.id, reference: task.reference, name: task.name},
                        item: {id: item.id, reference: item.reference, name: item.name},
                    }
                });
            });
        },
        async save() {
            Object.assign(this.item, this.requestForMaterials);
            this.debouncedUpdate();
        },
        async update() {
            this.busy = true;
            this.requestForMaterials.requestedFor = this.requestForMaterials.requestedFor ? moment(this.requestForMaterials.requestedFor).format("YYYY-MM-DD") : null;
            await this.requestForMaterials.update();
            const data = await RequestForMaterials.find(this.requestForMaterials.packageReference, this.requestForMaterials.id)
            this.requestForMaterials.lastUpdatedOn = data.lastUpdatedOn;
            this.requestForMaterials.lastUpdatedBy = data.lastUpdatedBy;
            this.requestForMaterials.lastUpdatedByReference = data.lastUpdatedByReference;
            Object.assign(this.item, this.requestForMaterials);
            this.busy = false;
        },
        addItem(trigger) {
            this.requestForMaterials.items.push({
                id: uuid(),
                quantity: this.newQuantity,
                uom: this.newUom,
                material: this.newMaterial,
            });
            this.$nextTick(() => {
                this.$refs.items[this.$refs.items.length - 1].$refs.material.disableSelectAll()
                this.$refs.items[this.$refs.items.length - 1].$refs.material.$el.focus()
                this.$refs.items[this.$refs.items.length - 1].$refs.material.enableSelectAll()
                this.newMaterial = '';
                this.newQuantity = '';
                this.newUom = ''
                this.update()
            })
        },
        minDate() {
            return null;
        },
        openDeleteModal() {
            const title = 'Are you sure you want to delete "Draft #' + this.requestForMaterials.referenceNumber + '"?'
            const description = 'Deleting this draft Request for Materials report is irreversible. Are you sure you want to delete it?'
            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.requestForMaterials.delete()
                this.removeItem(this.item)
            });
        },
        async submit() {
            await this.requestForMaterials.submit();
            Object.assign(this.item, this.requestForMaterials);
            this.busy = false;
            this.$emit("statusChanged", this.item, this.requestForMaterials);
        },
        removeMaterialItem(item) {
            this.requestForMaterials.items.splice(this.requestForMaterials.items.indexOf(item), 1);
            this.requestForMaterials.saveAndRefresh()
        },
        removeItemMaterialItem(item) {
            this.requestForMaterials.items = this.requestForMaterials.items.filter((i) => !i.itemReference || (i.itemReference.item.id !== item.itemReference.item.id))
            this.requestForMaterials.saveAndRefresh()
        },
        removeTaskMaterialItem(item) {
            this.requestForMaterials.items = this.requestForMaterials.items.filter((i) => !i.itemReference || (i.itemReference.task.id !== item.itemReference.task.id))
            this.requestForMaterials.saveAndRefresh()
        },
        removeSectionMaterialItem(item) {
            this.requestForMaterials.items = this.requestForMaterials.items.filter((i) => !i.itemReference || (i.itemReference.section.id !== item.itemReference.section.id))
            this.requestForMaterials.saveAndRefresh()
        },
    },
    async mounted() {
        this.requestForMaterials = await RequestForMaterials.find(this.packageReference, this.requestForMaterialsId);
        this.resourcePlanningReport = await ResourcePlanningReport.find(this.packageReference);
        this.state = 'loaded'
        this.contractRender++;
    },
}
</script>
